import React from "react";
import { MRT_ColumnDef } from "material-react-table";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { SearchFilterSort } from "../../../searchFilter/SearchFilterSort.Component";
import { DBTilte } from "../../modal/CloumnFilter/ExtensiveFilterContent";
import FilterPopup from "../../modal/CloumnFilter/FilterPopup";
import { INotificationTableList } from "../../../../slices/NotificationSlice";

interface UseNotificationColumnsProps {
  apiQuery: {
    sortField: string;
    isAscending: boolean;
  };
  searchFilter: {
    tempColumnSearch: {
      NUniqueID: { isFiltered: boolean };
      title: { isFiltered: boolean };
      category: { isFiltered: boolean };
      createdBy: { isFiltered: boolean };
      createdOn: { isFiltered: boolean };
      updatedBy: { isFiltered: boolean };
      updatedOn: { isFiltered: boolean };
      status: { isFiltered: boolean };
    };
  };
  handleSorting: (field: string) => void;
}

export const useNotificationColumns = ({
  apiQuery,
  searchFilter,
  handleSorting,
}: UseNotificationColumnsProps): MRT_ColumnDef<INotificationTableList>[] => {
  return [
    {
      accessorKey: "nUniqueID",
      header: "",
      grow: false,
      maxSize: 85,
      Header: () => (
        <div className="notify-table-align">
          <span>Notification ID</span>
          <SearchFilterSort
            fieldName={DBTilte.NUniqueID}
            currentSortFieldName={apiQuery.sortField}
            isAscending={apiQuery.isAscending}
            onClick={handleSorting}
          />
          <FilterPopup
            isFilter={searchFilter.tempColumnSearch.NUniqueID.isFiltered}
            searchField="Notification Unique ID"
            accessorKey="uniqueId"
          />
        </div>
      ),
      Cell: ({ cell }) => (
        <span className="table-row-content">{cell.getValue<string>()}</span>
      ),
    },
    {
      accessorKey: "title",
      header: "",
      grow: false,
      maxSize: 240,
      Header: () => (
        <div className="notify-table-align">
          <div>
            <span>Title</span>
            <SearchFilterSort
              fieldName={DBTilte.title}
              currentSortFieldName={apiQuery.sortField}
              isAscending={apiQuery.isAscending}
              onClick={handleSorting}
            />
          </div>
          <FilterPopup
            isFilter={searchFilter.tempColumnSearch.title.isFiltered}
            searchField="Title"
            accessorKey="title"
          />
        </div>
      ),
      Cell: ({ cell }) => (
        <span className="table-row-content" style={{ wordBreak: "break-word" }}>
          {cell.getValue<string>()}
        </span>
      ),
    },
    {
      accessorKey: "category",
      header: "",
      maxSize: 80,
      Header: () => (
        <div>
          <span style={{ marginRight: "8px" }}>Category</span>
          <FilterPopup
            isFilter={searchFilter.tempColumnSearch.category.isFiltered}
            accessorKey="category"
          />
        </div>
      ),
      size: 80,
      Cell: ({ cell }) => {
        const category = cell.getValue<string>();
        let className = "category";

        if (category === "CRITICAL") {
          className += " critical";
        } else if (category === "TARGET") {
          className += " target";
        } else if (category === "GENERAL") {
          className += " general";
        }

        return <span className={className}>{category}</span>;
      },
    },
    {
      accessorKey: "createdBy",
      header: "",
      maxSize: 140,
      Header: () => (
        <div className="notify-table-align">
          <div>
            <span>Created By</span>
            <SearchFilterSort
              fieldName={DBTilte.createdBy}
              currentSortFieldName={apiQuery.sortField}
              isAscending={apiQuery.isAscending}
              onClick={handleSorting}
            />
            <SearchFilterSort
              fieldName={DBTilte.createdOn}
              currentSortFieldName={apiQuery.sortField}
              isAscending={apiQuery.isAscending}
              onClick={handleSorting}
            />
          </div>
          <FilterPopup
            isFilter={
              searchFilter.tempColumnSearch.createdBy.isFiltered ||
              searchFilter.tempColumnSearch.createdOn.isFiltered
            }
            searchField="Created By"
            accessorKey="createdBy"
          />
        </div>
      ),
      Cell: ({ cell }) => {
        const cellValue = cell.getValue<{ name: string; dateTime: string }>();
        const formattedDate = dayjs(cellValue.dateTime).format(
          "MMM DD, YYYY hh:mm:ss A"
        );
        return (
          <div>
            <Tooltip title={cellValue.name}>
              <div className="table-row-content">
                {cellValue.name.slice(0, 18)}..
              </div>
            </Tooltip>
            <div className="timezone"> {formattedDate} </div>
          </div>
        );
      },
    },
    {
      accessorKey: "updatedBy",
      header: "",
      maxSize: 140,
      Header: () => (
        <div className="notify-table-align">
          <div>
            <span>Updated By</span>
            <SearchFilterSort
              fieldName={DBTilte.updatedBy}
              currentSortFieldName={apiQuery.sortField}
              isAscending={apiQuery.isAscending}
              onClick={handleSorting}
            />
            <SearchFilterSort
              fieldName={DBTilte.updatedOn}
              currentSortFieldName={apiQuery.sortField}
              isAscending={apiQuery.isAscending}
              onClick={handleSorting}
            />
          </div>
          <FilterPopup
            isFilter={
              searchFilter.tempColumnSearch.updatedBy.isFiltered ||
              searchFilter.tempColumnSearch.updatedOn.isFiltered
            }
            searchField="Updated By"
            accessorKey="updatedBy"
          />
        </div>
      ),
      Cell: ({ cell }) => {
        const cellValue = cell.getValue<{ name: string; dateTime: string }>();
        const formattedDate = cellValue.dateTime
          ? dayjs(cellValue.dateTime).format("MMM DD, YYYY hh:mm:ss A")
          : "-";
        return (
          <div>
            {cellValue.name ? (
              <>
                <Tooltip title={cellValue.name}>
                  <div className="table-row-content">
                    {cellValue.name.slice(0, 18)}..
                  </div>
                </Tooltip>
                <div className="timezone"> {formattedDate} </div>
              </>
            ) : (
              <div style={{ textAlign: "center" }}>-</div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "",
      Header: () => (
        <div>
          <span style={{ marginRight: "8px" }}>Status</span>
          <FilterPopup
            isFilter={searchFilter.tempColumnSearch.status.isFiltered}
            accessorKey="status"
          />
        </div>
      ),
      size: 80,
      Cell: ({ cell }) => {
        let status = cell.getValue<string>();
        if (status === "WAITING_SENT_FOR_PUBLICATION") {
          status = "WAITING";
        }
        let className = "status";

        if (status === "DRAFT") {
          className += " draft";
        } else if (status === "WAITING") {
          className += " waiting";
        } else if (status === "PUBLISHED") {
          className += " pulished";
        } else if (status === "RECALL") {
          className += " recall";
        } else if (status === "SUBMITTED") {
          className += " submitted";
        }

        return (
          <div className={className} style={{ paddingTop: "23px !important" }}>
            {status}
          </div>
        );
      },
    },
  ];
};
